import { IconButton, MenuItem, TextField } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useNavigate, useParams } from 'react-router-dom'
import './CatalogToolbar.css'
import { useEffect, useMemo, useState } from 'react'
import CatalogFilters from './CatalogFilter'
import {
  catalogColumnFilters,
  catalogColumns,
  catalogColumnType,
  catalogFilterColumnType,
  catalogTabGroups,
  catalogTabType,
} from './catalogTypes'
import { useCatalogData } from './useCatalogData'
import CatalogItemList from './CatalogItemList'

const CATALOG_GROUPS = [
  {
    label: 'GSA',
    options: [
      { label: 'GSA MAS', to: '/catalog/gsa/gs-35f-0002v' },
      { label: 'GSA - Open Market', to: '/catalog/gsa/gs-35f-0002v-open-market' },
      { label: 'GSA OLM', to: '/catalog/gsa/gs-35f-0002v-olm' },
      { label: 'GS-35F-0511T', to: '/catalog/gsa/gs-35f-0511t' },
      { label: '47QTCA23D00B9', to: '/catalog/gsa/47qtca23d00b9' },
      //{ label: '47QSW18D008F', to: '/catalog/gsa/47qsw18d008f' },
      //{ label: 'GS-35F-267DA', to: '/catalog/gsa/gs-35f-267da' },
      //{ label: 'GS-35F-0265X', to: '/catalog/gsa/gs-35f-0265x' },
      { label: 'GS-35F-0563U', to: '/catalog/gsa/gs-35f-0563u' },
      //{ label: '47QTCA26D0017', to: '/catalog/gsa/47qtca26d0017' },
      //{ label: 'GS-35F-688GA', to: '/catalog/gsa/gs-35f-688ga' },
      //{ label: 'GS-35F-303DA', to: '/catalog/gsa/gs-35f-303da' },
      { label: '47QTCA19D00MM', to: '/catalog/gsa/47qtca19d00mm' },
    ],
  },
  {
    label: 'SEWP V',
    options: [
      { label: 'SEWP B VIT', to: '/catalog/sewp/nng15sd41b' },
      { label: 'SEWP C IMPRES', to: '/catalog/sewp/nng15sc86b' },
    ],
  },
  {
    label: '2GIT',
    options: [
      { label: '47QTCA21A001G', to: '/catalog/2git/47qtca21a001g' },
      { label: '47QTCA21A0010', to: '/catalog/2git/47qtca21a0010' },
      { label: '47QTCA21A0026', to: '/catalog/2git/47qtca21a0026' },
      { label: '47QTCA21A002E', to: '/catalog/2git/47qtca21a002e' },
    ],
  },
  {
    label: 'GSA BPAs',
    options: [
      { label: 'HUD Software', to: '/catalog/gsabpa/86615424f00010' },
      { label: 'USDA Tablet BPA', to: '/catalog/gsabpa/12314425A0003' },
      { label: 'DOC NETS BPA', to: '/catalog/gsabpa/1331L525A13OS0016' },
      { label: 'DOL BPA', to: '/catalog/gsabpa/1605TB22A0001' },
      { label: 'HUD HW & UC BPA', to: '/catalog/gsabpa/86615424A00001' },
      { label: 'USDA Laptop BPA', to: '/catalog/gsabpa/12314425A0007' },
      { label: 'LogRhythm ESU/BPA', to: '/catalog/logrhythm' },
      { label: 'GSA - GSS', to: '/catalog/gsabpa/GS-35F-0002V-GSS' },
    ],
  },
  {
    label: 'ITES-4H',
    options: [{ label: 'W819TC25DA023', to: '/catalog/ites/w819tc25da023' }],
  },
  {
    label: 'NASPO',
    options: [{ label: 'AR3227', to: '/catalog/naspo/ar3227' }],
  },
]

export default function CatalogToolbar() {
  const navigate = useNavigate()
  const { header = '', contractNumber = '' } = useParams()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [defaultColumns, setDefaultColumns] = useState<catalogColumnType[]>([])
  const [activeTab, setActiveTab] = useState<catalogTabType>({ label: '', source: '' })
  const [filterColumns, setFilterColumns] = useState<catalogFilterColumnType[]>([])
  const [filterValues, setFilterValues] = useState<{ [k: string]: string }>({})
  const [manufacturerFilter, setManufacturerFilter] = useState<string | null>(null)
  const [mfrPartNumberFilter, setMfrPartNumberFilter] = useState('')

  const activeFilters = useMemo(
    () => ({
      ...(activeTab.filters ?? {}),
      ...filterValues,
      ...(manufacturerFilter ? { manufacturer: manufacturerFilter } : {}),
      ...(mfrPartNumberFilter ? { mfrPartNumber: mfrPartNumberFilter } : {}),
    }),
    [activeTab.filters, filterValues, manufacturerFilter, mfrPartNumberFilter],
  )

  const { catalogItems, total, loading, reload, updateProperty } = useCatalogData(
    activeTab.source,
    page,
    rowsPerPage,
    activeFilters,
    {
      modType: activeTab.modType,
    },
  )

  const [currentHeader, setCurrentHeader] = useState(header)
  const [currentContract, setCurrentContract] = useState(contractNumber)
  const [activeGroup, setActiveGroup] = useState<(typeof CATALOG_GROUPS)[number]>(
    CATALOG_GROUPS.find(group => group.label.toLowerCase() === currentHeader.toLowerCase()) ?? {
      label: '',
      options: [],
    },
  )
  const [activeOption, setActiveOption] = useState(
    activeGroup.options.find(opt => opt.to.split('/')[3] === currentContract) ?? { label: '', to: '' },
  )
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    setCurrentHeader(header)
    setCurrentContract(contractNumber)

    const newActiveGroup = CATALOG_GROUPS.find(group => group.options.some(opt => opt.to.split('/')[2] === header)) ?? {
      label: '',
      options: [],
    }
    setActiveGroup(newActiveGroup)
    setActiveOption(
      newActiveGroup.options.find(opt => opt.to.split('/')[3] === contractNumber) ?? { label: '', to: '' },
    )
  }, [header, contractNumber])

  useEffect(() => {
    const key = activeTab.label ? `${contractNumber}:${activeTab.label}` : contractNumber
    const cols = catalogColumns.get(header)?.get(key) ?? catalogColumns.get(header)?.get(contractNumber) ?? []
    const filters =
      catalogColumnFilters.get(header)?.get(key) ?? catalogColumnFilters.get(header)?.get(contractNumber) ?? []
    setDefaultColumns(cols)
    setFilterColumns(filters)
    setFilterValues(Object.fromEntries(filters.map(c => [c.key, ''])))
    setManufacturerFilter(null)
    setMfrPartNumberFilter('')
    setPage(0)
  }, [header, contractNumber, activeTab.label])

  useEffect(() => {
    document.title = `${activeGroup.label} Catalog | I-Quote`
  }, [activeGroup])

  return (
    <div className="catalog-page">
      <div className="catalog-toolbar">
        <TextField
          select
          size="small"
          label={activeGroup.label}
          value={activeOption.to}
          onChange={e => {
            setActiveOption(activeGroup.options.find(opt => opt.to === e.target.value) ?? { label: '', to: '' })
            navigate(e.target.value)
          }}
        >
          {activeGroup.options.map(opt => (
            <MenuItem key={opt.to} value={opt.to}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <div className="catalog-toolbar-actions">
          {activeTab.source.endsWith('/modifications') && (
            <IconButton aria-label="Refresh" onClick={() => reload()} disabled={loading === 'loading'}>
              <RefreshIcon />
            </IconButton>
          )}
          <IconButton aria-label="Open filter drawer" onClick={() => setFiltersOpen(true)}>
            <FilterListIcon />
          </IconButton>
        </div>
      </div>

      <CatalogFilters
        filters={filterValues}
        setFilter={setFilterValues}
        clearFilters={() => setFilterValues(Object.fromEntries(defaultColumns.map(column => [column.key, ''])))}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        columns={filterColumns}
      />

      <CatalogItemList
        header={header}
        contractNumber={contractNumber}
        catalogItems={catalogItems}
        updateProperty={updateProperty}
        columnMapping={catalogColumns.get(header) ?? new Map()}
        tabGroups={catalogTabGroups.get(header) ?? new Map()}
        onTabChange={setActiveTab}
        total={total}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        reload={reload}
        isModification={activeTab.isModification ?? false}
        manufacturerFilter={manufacturerFilter}
        onManufacturerFilterChange={setManufacturerFilter}
        mfrPartNumberFilter={mfrPartNumberFilter}
        onMfrPartNumberFilterChange={setMfrPartNumberFilter}
      />
    </div>
  )
}
